import { readFile, access, readdir } from 'node:fs/promises';
import { join } from 'node:path';
import type { CheckResult } from '../types/index.js';

export async function checkGitignore(cwd: string): Promise<CheckResult[]> {
  const results: CheckResult[] = [];
  const gitignorePath = join(cwd, '.gitignore');

  try {
    await access(gitignorePath);
    const content = await readFile(gitignorePath, 'utf-8');

    const requiredPatterns = ['.env', 'node_modules', '.env.local', '.env*.local'];
    const missingPatterns = requiredPatterns.filter(
      (pattern) => !content.includes(pattern)
    );

    if (missingPatterns.length > 0) {
      results.push({
        ruleId: 'secrets.gitignore-required',
        severity: 'warn',
        message: `.gitignore is missing patterns: ${missingPatterns.join(', ')}`,
        file: '.gitignore',
        fix: {
          description: `Add missing patterns to .gitignore`,
          replacement: missingPatterns.join('\n'),
        },
      });
    }
  } catch {
    results.push({
      ruleId: 'secrets.gitignore-required',
      severity: 'warn',
      message: '.gitignore file not found',
      fix: {
        description: 'Create .gitignore with common patterns',
      },
    });
  }

  return results;
}

export async function checkEnvFiles(cwd: string): Promise<CheckResult[]> {
  const results: CheckResult[] = [];

  try {
    const files = await readdir(cwd);
    const envFiles = files.filter(
      (f) => f.startsWith('.env') && !f.endsWith('.example') && !f.endsWith('.template')
    );

    const gitignorePath = join(cwd, '.gitignore');
    let gitignoreContent = '';
    try {
      gitignoreContent = await readFile(gitignorePath, 'utf-8');
    } catch {
      // No gitignore
    }

    for (const envFile of envFiles) {
      const isIgnored = gitignoreContent.includes(envFile) || gitignoreContent.includes('.env');
      if (!isIgnored) {
        results.push({
          ruleId: 'secrets.no-env-commit',
          severity: 'error',
          message: `${envFile} may be committed to git. Add it to .gitignore.`,
          file: envFile,
          fix: {
            description: `Add ${envFile} to .gitignore`,
          },
        });
      }
    }
  } catch {
    // Directory read error, skip
  }

  return results;
}

export async function checkLockfile(cwd: string): Promise<CheckResult[]> {
  const results: CheckResult[] = [];
  const lockfiles = ['package-lock.json', 'yarn.lock', 'pnpm-lock.yaml', 'bun.lockb'];

  try {
    const files = await readdir(cwd);
    const hasLockfile = lockfiles.some((lf) => files.includes(lf));
    const hasPackageJson = files.includes('package.json');

    if (hasPackageJson && !hasLockfile) {
      results.push({
        ruleId: 'deps.lockfile.required',
        severity: 'error',
        message: 'No lockfile found. Run npm install, yarn, or pnpm install.',
        fix: {
          description: 'Generate lockfile by running package manager install',
        },
      });
    }
  } catch {
    // Skip
  }

  return results;
}

export async function checkUnpinnedDeps(cwd: string): Promise<CheckResult[]> {
  const results: CheckResult[] = [];
  const packageJsonPath = join(cwd, 'package.json');

  try {
    const content = await readFile(packageJsonPath, 'utf-8');
    const pkg = JSON.parse(content);

    const checkDeps = (deps: Record<string, string> | undefined, type: string) => {
      if (!deps) return;
      for (const [name, version] of Object.entries(deps)) {
        if (version.startsWith('^') || version.startsWith('~') || version === '*' || version === 'latest') {
          results.push({
            ruleId: 'deps.no-unpinned',
            severity: 'warn',
            message: `${type} "${name}" has unpinned version: ${version}`,
            file: 'package.json',
          });
        }
      }
    };

    checkDeps(pkg.dependencies, 'Dependency');
    checkDeps(pkg.devDependencies, 'DevDependency');
  } catch {
    // Skip
  }

  return results;
}

export async function checkGitDeps(cwd: string): Promise<CheckResult[]> {
  const results: CheckResult[] = [];
  const packageJsonPath = join(cwd, 'package.json');

  try {
    const content = await readFile(packageJsonPath, 'utf-8');
    const pkg = JSON.parse(content);

    const checkDeps = (deps: Record<string, string> | undefined) => {
      if (!deps) return;
      for (const [name, version] of Object.entries(deps)) {
        if (
          version.startsWith('git://') ||
          version.startsWith('git+') ||
          version.startsWith('github:') ||
          version.includes('github.com')
        ) {
          results.push({
            ruleId: 'deps.no-git-deps',
            severity: 'warn',
            message: `Dependency "${name}" uses git URL: ${version}`,
            file: 'package.json',
          });
        }
      }
    };

    checkDeps(pkg.dependencies);
    checkDeps(pkg.devDependencies);
  } catch {
    // Skip
  }

  return results;
}

export async function checkTestsExist(cwd: string): Promise<CheckResult[]> {
  const results: CheckResult[] = [];

  try {
    const testPatterns = ['test', 'tests', '__tests__', 'spec', 'specs'];
    const files = await readdir(cwd);

    const hasTestDir = testPatterns.some((p) => files.includes(p));
    const hasTestFiles = files.some(
      (f) => f.endsWith('.test.ts') || f.endsWith('.test.js') || f.endsWith('.spec.ts') || f.endsWith('.spec.js')
    );

    if (!hasTestDir && !hasTestFiles) {
      results.push({
        ruleId: 'tests.unit.required',
        severity: 'warn',
        message: 'No test files or test directory found',
      });
    }
  } catch {
    // Skip
  }

  return results;
}

export async function checkTsConfig(cwd: string): Promise<CheckResult[]> {
  const results: CheckResult[] = [];
  const tsconfigPath = join(cwd, 'tsconfig.json');

  try {
    const content = await readFile(tsconfigPath, 'utf-8');
    const tsconfig = JSON.parse(content);

    if (!tsconfig.compilerOptions?.strict) {
      results.push({
        ruleId: 'code.strict-mode',
        severity: 'warn',
        message: 'TypeScript strict mode is not enabled',
        file: 'tsconfig.json',
        fix: {
          description: 'Add "strict": true to compilerOptions',
        },
      });
    }
  } catch {
    // No tsconfig or parse error
  }

  return results;
}

export async function checkNodeVersion(cwd: string): Promise<CheckResult[]> {
  const results: CheckResult[] = [];

  try {
    const files = await readdir(cwd);
    const hasNvmrc = files.includes('.nvmrc');
    const hasNodeVersion = files.includes('.node-version');

    let hasEngines = false;
    try {
      const pkgContent = await readFile(join(cwd, 'package.json'), 'utf-8');
      const pkg = JSON.parse(pkgContent);
      hasEngines = !!pkg.engines?.node;
    } catch {
      // No package.json
    }

    if (!hasNvmrc && !hasNodeVersion && !hasEngines) {
      results.push({
        ruleId: 'config.node-version',
        severity: 'info',
        message: 'Node.js version not specified (.nvmrc, .node-version, or engines.node)',
        fix: {
          description: 'Create .nvmrc or add engines.node to package.json',
        },
      });
    }
  } catch {
    // Skip
  }

  return results;
}

export async function checkEditorConfig(cwd: string): Promise<CheckResult[]> {
  const results: CheckResult[] = [];

  try {
    await access(join(cwd, '.editorconfig'));
  } catch {
    results.push({
      ruleId: 'config.editor-config',
      severity: 'info',
      message: '.editorconfig not found',
      fix: {
        description: 'Create .editorconfig for consistent formatting',
      },
    });
  }

  return results;
}

export async function runBuiltinChecks(cwd: string, ruleIds: string[]): Promise<CheckResult[]> {
  const results: CheckResult[] = [];

  const checks: Record<string, () => Promise<CheckResult[]>> = {
    'secrets.gitignore-required': () => checkGitignore(cwd),
    'secrets.no-env-commit': () => checkEnvFiles(cwd),
    'deps.lockfile.required': () => checkLockfile(cwd),
    'deps.no-unpinned': () => checkUnpinnedDeps(cwd),
    'deps.no-git-deps': () => checkGitDeps(cwd),
    'tests.unit.required': () => checkTestsExist(cwd),
    'code.strict-mode': () => checkTsConfig(cwd),
    'config.node-version': () => checkNodeVersion(cwd),
    'config.editor-config': () => checkEditorConfig(cwd),
  };

  for (const ruleId of ruleIds) {
    const check = checks[ruleId];
    if (check) {
      const checkResults = await check();
      results.push(...checkResults);
    }
  }

  return results;
}
